import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI } from '../../lib/api';
import { fmtDateTimeInTz } from './meetingsUtils';
import CompanyPicker from './CompanyPicker';

// Round 3 (31 Oct 2026, Visits & Meetings): every visit still carrying an untagged
// company and/or a typed attendee name — the only place these surface until tagged.
// Tagging never re-derives counterparty_role; it's exactly as originally chosen.
function TagAttendeeControl({ meetingId, name, onTagged }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [colleagues, setColleagues] = useState([]);
  const [visitors, setVisitors] = useState([]);

  useEffect(() => {
    if (!open) return;
    meetingsAPI.listFieldVisitors().then((r) => setVisitors(r.data.field_visitors || [])).catch(() => {});
  }, [open]);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setColleagues([]); return; }
    try { setColleagues((await meetingsAPI.searchColleagues(q)).data.colleagues || []); } catch { setColleagues([]); }
  };

  const tagTo = async (targetType, targetId) => {
    try {
      await meetingsAPI.tagAttendee(meetingId, { typed_name: name, target_type: targetType, target_id: targetId });
      toast.success(`Tagged "${name}"`);
      setOpen(false);
      onTagged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to tag attendee'); }
  };

  if (!open) {
    return <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`tag-attendee-btn-${meetingId}-${name}`}>Tag</Button>;
  }
  const filteredVisitors = visitors.filter((v) => v.active && (!query || v.name.toLowerCase().includes(query.toLowerCase())));
  return (
    <div className="border rounded-sm p-2 space-y-1.5 w-64" data-testid={`tag-attendee-form-${meetingId}-${name}`}>
      <Input placeholder="Search colleague or field visitor..." value={query} onChange={(e) => search(e.target.value)}
        data-testid={`tag-attendee-search-${meetingId}-${name}`} />
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {colleagues.map((c) => (
          <button key={c.id} type="button" data-testid={`tag-attendee-option-user-${c.id}`}
            className="w-full text-left text-sm px-2 py-1 hover:bg-muted/60 rounded-sm" onClick={() => tagTo('USER', c.id)}>{c.name}</button>
        ))}
        {filteredVisitors.map((v) => (
          <button key={v.id} type="button" data-testid={`tag-attendee-option-visitor-${v.id}`}
            className="w-full text-left text-sm px-2 py-1 hover:bg-muted/60 rounded-sm" onClick={() => tagTo('VISITOR', v.id)}>{v.name} (field visitor)</button>
        ))}
      </div>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`tag-attendee-cancel-${meetingId}-${name}`}>Cancel</Button>
    </div>
  );
}

function ToTagMeetingRow({ meeting, onTagged }) {
  const [tagCompanyOpen, setTagCompanyOpen] = useState(false);
  const isUntaggedCompany = meeting.company?.type === 'UNTAGGED';
  const untaggedNames = meeting.attendee_names_untagged || [];

  const onPickCompany = async (val) => {
    if (!val || val.type === 'UNTAGGED') return;
    try {
      await meetingsAPI.tagCompany(meeting.id, { type: val.type, ref: val.ref });
      toast.success('Company tagged');
      setTagCompanyOpen(false);
      onTagged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to tag company'); }
  };

  return (
    <Card className="rounded-sm" data-testid={`to-tag-row-${meeting.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="font-medium text-sm">{meeting.company?.name_at_meeting}</p>
            <p className="text-xs text-muted-foreground">{fmtDateTimeInTz(meeting.start_at, meeting.tz)} · {meeting.organiser_name}</p>
          </div>
          {isUntaggedCompany && (
            tagCompanyOpen ? (
              <div className="w-72">
                <CompanyPicker value={null} onChange={onPickCompany} label="Tag to" testIdPrefix={`to-tag-company-${meeting.id}`} allowUntagged={false} />
                <Button size="sm" variant="ghost" onClick={() => setTagCompanyOpen(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setTagCompanyOpen(true)} data-testid={`tag-company-btn-${meeting.id}`}>Tag company</Button>
            )
          )}
        </div>
        {untaggedNames.length > 0 && (
          <div className="flex flex-wrap items-start gap-2">
            <span className="text-xs text-muted-foreground">Typed attendees:</span>
            {untaggedNames.map((n) => (
              <div key={n} className="flex items-center gap-1.5">
                <Badge variant="outline" className="border-dashed">{n}</Badge>
                <TagAttendeeControl meetingId={meeting.id} name={n} onTagged={onTagged} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function VisitsToTagTab({ capabilities }) {
  const [scope, setScope] = useState('mine');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMeetings((await meetingsAPI.toTag(scope)).data.meetings || []); }
    catch { toast.error('Failed to load visits to tag'); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const scopeOptions = [['mine', 'Mine']];
  if (capabilities.is_manager) scopeOptions.push(['team', 'My team']);
  if (capabilities.can_admin) scopeOptions.push(['all', 'Everyone']);

  return (
    <div className="space-y-4" data-testid="visits-to-tag-tab">
      {scopeOptions.length > 1 && (
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-44" data-testid="to-tag-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>{scopeOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
      ) : meetings.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-visits-to-tag-msg">Nothing to tag.</p>
      ) : (
        <div className="space-y-2">{meetings.map((m) => <ToTagMeetingRow key={m.id} meeting={m} onTagged={load} />)}</div>
      )}
    </div>
  );
}
